import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { Currency } from "_shared/money/currencies/currency";
import { z } from "zod";

export type InsuranceInfoJSON = z.infer<typeof InsuranceInfoSchema.InsuranceInfoJSONZ>;
export type InsuranceInfoParams = z.infer<typeof InsuranceInfoSchema.InsuranceInfoZ>;

export default class InsuranceInfoSchema {
  public static readonly InsuranceInfoJSONZ = z.object({
    insuredAmount: z.number().nullish(),
    currency: z.string().nullish(),
  });

  public static readonly InsuranceInfoZ = z.object({
    insuredAmount: z.number().optional(),
    currency: z.enum(Currency).optional(),
  });

  public static validate(values: unknown): InsuranceInfoParams {
    return parseOrThrow(InsuranceInfoSchema.InsuranceInfoZ, values, {
      logError: true,
      schemaName: "InsuranceInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): InsuranceInfoJSON {
    return parseOrThrow(InsuranceInfoSchema.InsuranceInfoJSONZ, json, {
      logError: true,
      schemaName: "InsuranceInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is InsuranceInfoParams {
    try {
      InsuranceInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is InsuranceInfoJSON {
    try {
      InsuranceInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): InsuranceInfoParams {
    const parsed = parseOrThrow(InsuranceInfoSchema.InsuranceInfoJSONZ, json, {
      logError: true,
      schemaName: "InsuranceInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return InsuranceInfoSchema.codec.decode(parsed);
  }

  public static serialize(params: InsuranceInfoParams): InsuranceInfoJSON {
    const parsed = InsuranceInfoSchema.validate(params);
    return InsuranceInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(InsuranceInfoSchema.InsuranceInfoJSONZ, InsuranceInfoSchema.InsuranceInfoZ, {
    decode: (json) => ({
      insuredAmount: json.insuredAmount ?? undefined,
      currency: json.currency ? Currency[json.currency as keyof typeof Currency] : Currency.USD,
    }),
    encode: (params) => ({
      insuredAmount: params.insuredAmount,
      currency: params.currency,
    }),
  });
}
